import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { MessageCircle, Send, X, Loader2, Bot, User } from 'lucide-react';
import axios from 'axios';
import { resolveBackendBase } from '../utils/backendBase.js';
const API_URL = ("production" === 'production'
    ? '/api'
    : `${resolveBackendBase() || ''}/api`.replace('//api', '/api'));
const CustomerChatbot = ({ vehicleId, vehicleInfo }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: 'مرحباً! أنا مساعد الورشة الذكي. كيف يمكنني مساعدتك؟\n\nيمكنك السؤال عن:\n- حالة المركبة\n- تفاصيل الخدمات\n- الأسعار المتوقعة\n- أي استفسار آخر',
            timestamp: new Date()
        }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    const messagesEndRef = useRef(null);
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    useEffect(() => {
        scrollToBottom();
    }, [messages]);
    const sendMessage = async () => {
        if (!input.trim() || loading)
            return;
        const userMessage = input.trim();
        setInput('');
        // Add user message
        setMessages(prev => [...prev, {
                role: 'user',
                content: userMessage,
                timestamp: new Date()
            }]);
        setLoading(true);
        try {
            // Call AI endpoint with vehicle context
            const response = await axios.post(`${API_URL}/ai/enhanced-chat`, {
                message: userMessage,
                session_id: sessionId,
                vehicle_info: vehicleInfo || `Vehicle ID: ${vehicleId}`,
                provider: 'anthropic'
            });
            // Add AI response
            setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: response.data.response || response.data.text || 'عذراً، لم أتمكن من الرد على سؤالك.',
                    timestamp: new Date()
                }]);
        }
        catch (error) {
            console.error('Chat error:', error);
            setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: 'عذراً، حدث خطأ. الرجاء المحاولة مرة أخرى.',
                    timestamp: new Date()
                }]);
        }
        finally {
            setLoading(false);
        }
    };
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };
    return (_jsxs(_Fragment, { children: [!isOpen && (_jsx(Button, { onClick: () => setIsOpen(true), className: "fixed bottom-6 left-6 rounded-full w-16 h-16 bg-blue-600 hover:bg-blue-700 shadow-lg z-50 flex items-center justify-center", children: _jsx(MessageCircle, { size: 28 }) })), isOpen && (_jsxs(Card, { className: "fixed bottom-6 left-6 w-96 h-[600px] shadow-2xl z-50 flex flex-col", children: [_jsxs(CardHeader, { className: "bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex flex-row items-center justify-between rounded-t-lg", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Bot, { size: 24 }), _jsx(CardTitle, { className: "text-lg", children: "\u0645\u0633\u0627\u0639\u062F \u0627\u0644\u0648\u0631\u0634\u0629 \u0627\u0644\u0630\u0643\u064A" })] }), _jsx(Button, { variant: "ghost", size: "icon", onClick: () => setIsOpen(false), className: "text-white hover:bg-blue-800", children: _jsx(X, { size: 20 }) })] }), _jsxs(CardContent, { className: "flex-1 flex flex-col p-0", children: [_jsxs("div", { className: "flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50", dir: "rtl", children: [messages.map((msg, idx) => (_jsxs("div", { className: `flex gap-2 ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`, children: [msg.role === 'assistant' && (_jsx("div", { className: "w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0", children: _jsx(Bot, { size: 16, className: "text-white" }) })), _jsxs("div", { className: `max-w-[75%] rounded-lg p-3 ${msg.role === 'user'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-white border border-slate-200 text-slate-800'}`, children: [_jsx("p", { className: "text-sm whitespace-pre-wrap", children: msg.content }), _jsx("p", { className: "text-xs mt-1 opacity-60", children: msg.timestamp.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' }) })] }), msg.role === 'user' && (_jsx("div", { className: "w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0", children: _jsx(User, { size: 16, className: "text-white" }) }))] }, idx))), loading && (_jsx("div", { className: "flex justify-end gap-2", children: _jsx("div", { className: "bg-white border border-slate-200 rounded-lg p-3", children: _jsx(Loader2, { className: "animate-spin text-blue-600", size: 20 }) }) })), _jsx("div", { ref: messagesEndRef })] }), _jsx("div", { className: "p-4 border-t bg-white", children: _jsxs("div", { className: "flex gap-2", children: [_jsx(Input, { value: input, onChange: (e) => setInput(e.target.value), onKeyPress: handleKeyPress, placeholder: "\u0627\u0643\u062A\u0628 \u0633\u0624\u0627\u0644\u0643 \u0647\u0646\u0627...", className: "flex-1", disabled: loading, dir: "rtl" }), _jsx(Button, { onClick: sendMessage, disabled: loading || !input.trim(), className: "bg-blue-600 hover:bg-blue-700", children: _jsx(Send, { size: 18 }) })] }) })] })] }))] }));
};
export default CustomerChatbot;
